import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./Navigation.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCart } from "../../Context/Cart";
import { useAuth } from "../../Context/Auth";
import { useLanguage } from "../../Context/Language";
import {
  getNotificationCountStorage,
  setNotificationCountStorage,
  clearNotificationCountStorage,
} from "../../Utils/storage";
import {
  getNotificationCount,
  getHomeNotifications,
  markNotificationRead,
} from "../../Api/apiService";
import { getRelativeTime } from "../../Utils/formatUtils";
import {
  faShoppingCart,
  faUser,
  faBars,
  faTimes,
  faSignOutAlt,
  faBoxOpen,
  faCog,
  faHeart,
  faRightToBracket,
  faUserPlus,
  faBell,
  faEnvelope,
  faSpinner,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import usflag from "../../Images/us.png";
import swflag from "../../Images/sw.png";
import logo from "../../Images/ferramentum-logo.png";

const Navigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(() =>
    getNotificationCountStorage(),
  );
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [markingRead, setMarkingRead] = useState({});
  const [markingAll, setMarkingAll] = useState(false);

  const accountRef = useRef(null);
  const notificationsRef = useRef(null);
  const languageRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount, toggleCart, closeCart: closeCartUI } = useCart();
  const { user, loading, logout } = useAuth();
  const { language, changeLanguage, t, translateNotification } = useLanguage();

  const getDisplayName = (userObj) => {
    if (!userObj) return null;
    if (userObj.name) return userObj.name;
    if (userObj.first_name && userObj.last_name)
      return `${userObj.first_name} ${userObj.last_name}`;
    if (userObj.first_name) return userObj.first_name;
    if (userObj.username) return userObj.username;
    return null;
  };

  const getStoredNotificationCount = () => getNotificationCountStorage();
  const setStoredNotificationCount = (count) => {
    setNotificationCount(count);
    setNotificationCountStorage(count);
  };
  const clearStoredNotificationCount = () => {
    setNotificationCount(0);
    clearNotificationCountStorage();
  };

  const extractNotificationCount = (data) => {
    if (typeof data?.unreadCount === "number") return data.unreadCount;
    if (typeof data?.count === "number") return data.count;
    if (typeof data?.data?.unreadCount === "number")
      return data.data.unreadCount;
    if (typeof data?.data?.count === "number") return data.data.count;
    return null;
  };

  const closeAllDropdowns = () => {
    setIsAccountOpen(false);
    setIsNotificationsOpen(false);
    setIsLanguageOpen(false);
  };

  const toggleMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      closeAllDropdowns();
      closeCartUI();
    }
  };

  const closeMenu = () => setIsMobileMenuOpen(false);

  const toggleLanguage = () => {
    setIsLanguageOpen(!isLanguageOpen);
    if (!isLanguageOpen) {
      setIsAccountOpen(false);
      setIsNotificationsOpen(false);
      setIsMobileMenuOpen(false);
      closeCartUI();
    }
  };

  const handleLanguageSelect = (lang) => {
    changeLanguage(lang);
    setIsLanguageOpen(false);
  };

  const toggleAccount = () => {
    setIsAccountOpen(!isAccountOpen);
    if (!isAccountOpen) {
      setIsNotificationsOpen(false);
      setIsLanguageOpen(false);
      setIsMobileMenuOpen(false);
      closeCartUI();
    }
  };

  const fetchNotificationCount = useCallback(async () => {
    try {
      const data = await getNotificationCount();
      const count = extractNotificationCount(data);
      if (typeof count === "number") {
        setStoredNotificationCount(count);
      }
    } catch (error) {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoadingNotifications(true);
    try {
      const data = await getHomeNotifications();
      let allNotifications = [];
      if (data && data.notifications && Array.isArray(data.notifications)) {
        allNotifications = data.notifications;
      } else if (Array.isArray(data)) {
        allNotifications = data;
      } else if (
        data?.data?.notifications &&
        Array.isArray(data.data.notifications)
      ) {
        allNotifications = data.data.notifications;
      } else if (Array.isArray(data?.data)) {
        allNotifications = data.data;
      }
      const sorted = [...allNotifications].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
      setNotifications(sorted.slice(0, 3));
      const countFromResponse = extractNotificationCount(data);
      if (typeof countFromResponse === "number") {
        setStoredNotificationCount(countFromResponse);
      } else if (allNotifications.length > 0) {
        const unreadCount = allNotifications.filter(
          (n) => n.is_read === 0,
        ).length;
        setStoredNotificationCount(unreadCount);
      }
    } catch (error) {
      console.error("Failed to fetch notifications", error);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const toggleNotifications = async () => {
    const willOpen = !isNotificationsOpen;
    setIsNotificationsOpen(willOpen);
    if (willOpen) {
      setIsAccountOpen(false);
      setIsLanguageOpen(false);
      setIsMobileMenuOpen(false);
      closeCartUI();
      await fetchNotifications();
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    setMarkingRead((prev) => ({ ...prev, [notificationId]: true }));
    try {
      await markNotificationRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: 1 } : n)),
      );
      const nextCount = Math.max(0, getStoredNotificationCount() - 1);
      setStoredNotificationCount(nextCount);
      await fetchNotificationCount();
    } catch (error) {
      console.error("Failed to mark as read", error);
    } finally {
      setMarkingRead((prev) => ({ ...prev, [notificationId]: false }));
    }
  };

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    const unreadIds = notifications.reduce((acc, n) => {
      if (n.is_read === 0) acc.push(n.id);
      return acc;
    }, []);
    let successCount = 0;
    for (const id of unreadIds) {
      try {
        await markNotificationRead(id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)),
        );
        successCount++;
      } catch (err) {
        console.error(`Failed to mark ${id}`, err);
      }
    }
    if (successCount > 0) {
      const nextCount = Math.max(
        0,
        getStoredNotificationCount() - successCount,
      );
      setStoredNotificationCount(nextCount);
      await fetchNotificationCount();
    }
    setMarkingAll(false);
  };

  const handleCartClick = () => {
    toggleCart();
    closeAllDropdowns();
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: "Home", href: "/" },
    { name: "Products", href: "/products" },
    { name: "Categories", href: "/categories" },
    { name: "Deals", href: "/deals" },
    { name: "Contact", href: "/contact" },
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        setIsAccountOpen(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(e.target)
      ) {
        setIsNotificationsOpen(false);
      }
      if (languageRef.current && !languageRef.current.contains(e.target)) {
        setIsLanguageOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        closeAllDropdowns();
      }
    };
    document.addEventListener("click", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleLogout = () => {
    logout();
    clearStoredNotificationCount();
    closeAllDropdowns();
    navigate("/");
  };

  const getInitials = (userObj) => {
    const name = getDisplayName(userObj);
    if (!name) return "?";
    return name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const displayUser = user;
  const displayName = getDisplayName(displayUser);
  const isLoggedIn = !!displayUser;
  const isUserLoading = loading;
  const notificationRefreshRef = useRef(0);

  useEffect(() => {
    if (isLoggedIn) {
      const now = Date.now();
      if (now - notificationRefreshRef.current > 15000) {
        setNotificationCount(getNotificationCountStorage());
        fetchNotificationCount();
        notificationRefreshRef.current = now;
      }
    } else {
      clearStoredNotificationCount();
    }
  }, [location.pathname, isLoggedIn, fetchNotificationCount]);

  useEffect(() => {
    if (!isLoggedIn) return;
    const handleFocus = () => fetchNotificationCount();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isLoggedIn, fetchNotificationCount]);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo" onClick={closeMenu}>
          <img src={logo} alt="Ferramentum" />
        </Link>

        <ul className={`nav-menu ${isMobileMenuOpen ? "active" : ""}`}>
          {navItems.map((item) => (
            <li key={item.name} className="nav-item">
              <Link to={item.href} className="nav-link" onClick={closeMenu}>
                {t(item.name)}
              </Link>
            </li>
          ))}
        </ul>

        <div className="nav-actions">
          <div className="language-wrapper" ref={languageRef}>
            <button
              type="button"
              className="nav-lang-btn"
              onClick={toggleLanguage}
              aria-label="Language selector"
              aria-expanded={isLanguageOpen}
            >
              <img
                src={language === "sv" ? swflag : usflag}
                alt="flags"
                className="option-flag-img"
              />
              <span className="lang-label">
                {language === "sv" ? "Svenska" : "English"}
              </span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`lang-chevron ${isLanguageOpen ? "open" : ""}`}
              />
            </button>

            {isLanguageOpen && (
              <div className="language-dropdown">
                <button
                  type="button"
                  className={`lang-option ${language === "sv" ? "active" : ""}`}
                  onClick={() => handleLanguageSelect("sv")}
                >
                  <img src={swflag} alt="Svenska" className="option-flag-img" />
                  <span className="option-text">Svenska</span>
                </button>
                <button
                  type="button"
                  className={`lang-option ${language === "en" ? "active" : ""}`}
                  onClick={() => handleLanguageSelect("en")}
                >
                  <img src={usflag} alt="English" className="option-flag-img" />
                  <span className="option-text">English</span>
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            className="nav-cart-btn"
            aria-label="Open cart"
            onClick={handleCartClick}
          >
            <FontAwesomeIcon icon={faShoppingCart} />
            {cartCount > 0 && (
              <span className="nav-cart-badge">{cartCount}</span>
            )}
          </button>

          {isLoggedIn && (
            <div className="notifications-wrapper" ref={notificationsRef}>
              <button
                type="button"
                className="nav-notifications-btn"
                onClick={toggleNotifications}
                aria-label="Notifications"
              >
                <FontAwesomeIcon icon={faBell} />
                {notificationCount > 0 && (
                  <span className="nav-notifications-badge">
                    {notificationCount}
                  </span>
                )}
              </button>
              {isNotificationsOpen && (
                <div className="notifications-dropdown">
                  <div className="notifications-header">
                    <span>{t("Notifications")}</span>
                    {notificationCount > 0 && (
                      <button
                        className="mark-all-read"
                        onClick={handleMarkAllAsRead}
                        disabled={markingAll}
                      >
                        {markingAll ? (
                          <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                          t("Mark all read")
                        )}
                      </button>
                    )}
                  </div>
                  <div className="notifications-list">
                    {loadingNotifications ? (
                      <div className="notifications-loading">
                        <FontAwesomeIcon icon={faSpinner} spin /> {t("Loading")}
                        ...
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="notifications-empty">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <p>{t("No notifications yet")}</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <div
                          key={notif.id}
                          className={`notification-item ${notif.is_read === 0 ? "unread" : ""}`}
                        >
                          <div className="notification-icon">
                            <FontAwesomeIcon icon={faBell} />
                          </div>
                          <div className="notification-content">
                            {notif.title && (
                              <strong className="notification-title">
                                {translateNotification(notif.title)}{" "}
                                {/* translated title */}
                              </strong>
                            )}
                            <p className="notification-message">
                              {translateNotification(notif.message)}{" "}
                              {/* translated message */}
                            </p>
                            <span className="notification-time">
                              {getRelativeTime(notif.created_at)}
                            </span>
                          </div>
                          {notif.is_read === 0 && (
                            <button
                              className="mark-read-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notif.id);
                              }}
                              disabled={markingRead[notif.id]}
                            >
                              {markingRead[notif.id] ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                              ) : (
                                t("Mark Read")
                              )}
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  <Link
                    to="/settings?tab=notifications"
                    className="notifications-footer"
                    onClick={() => setIsNotificationsOpen(false)}
                  >
                    {t("View all notifications")}
                  </Link>
                </div>
              )}
            </div>
          )}

          <div className="account-wrapper" ref={accountRef}>
            <button
              type="button"
              className="nav-account-btn"
              onClick={toggleAccount}
              aria-label="Account"
            >
              {!isUserLoading && isLoggedIn ? (
                <div className="account-avatar">{getInitials(displayUser)}</div>
              ) : (
                <div className="account-avatar account-avatar-guest">
                  <FontAwesomeIcon icon={faUser} />
                </div>
              )}
              <span className="account-label">
                {isUserLoading
                  ? "..."
                  : isLoggedIn
                    ? displayName
                    : t("Account")}
              </span>
            </button>
            {isAccountOpen && (
              <div className="account-dropdown">
                {!isUserLoading && isLoggedIn ? (
                  <>
                    <div className="dropdown-header">
                      <div className="dropdown-avatar">
                        {getInitials(displayUser)}
                      </div>
                      <div className="dropdown-user-info">
                        <span className="dropdown-name">{displayName}</span>
                        <span className="dropdown-email">
                          {displayUser.email || displayUser.email_address || ""}
                        </span>
                      </div>
                    </div>
                    <div className="dropdown-divider" />
                    <Link
                      to="/orders"
                      className="dropdown-item"
                      onClick={() => setIsAccountOpen(false)}
                    >
                      <FontAwesomeIcon icon={faBoxOpen} />
                      <span>{t("My Orders")}</span>
                    </Link>
                    <Link
                      to="/wishlist"
                      className="dropdown-item"
                      onClick={() => setIsAccountOpen(false)}
                    >
                      <FontAwesomeIcon icon={faHeart} />
                      <span>{t("Wishlist")}</span>
                    </Link>
                    <Link
                      to="/settings"
                      className="dropdown-item"
                      onClick={() => setIsAccountOpen(false)}
                    >
                      <FontAwesomeIcon icon={faCog} />
                      <span>{t("Settings")}</span>
                    </Link>
                    <div className="dropdown-divider" />
                    <button
                      type="button"
                      className="dropdown-item dropdown-logout"
                      onClick={handleLogout}
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      <span>{t("Logout")}</span>
                    </button>
                  </>
                ) : (
                  <div className="dropdown-guest">
                    <div className="guest-icon">
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <p className="guest-title">{t("Welcome to Ferramentum")}</p>
                    <p className="guest-subtitle">
                      {t("Sign in to track orders, save wishlist & more")}
                    </p>
                    <Link
                      to="/login"
                      className="guest-btn guest-login"
                      onClick={() => setIsAccountOpen(false)}
                    >
                      <FontAwesomeIcon icon={faRightToBracket} />
                      <span>{t("Login")}</span>
                    </Link>
                    <Link
                      to="/register"
                      className="guest-btn guest-register"
                      onClick={() => setIsAccountOpen(false)}
                    >
                      <FontAwesomeIcon icon={faUserPlus} />
                      <span>{t("Create Account")}</span>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            className="menu-toggle"
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
          </button>
        </div>
      </div>

      {(isMobileMenuOpen || isAccountOpen || isNotificationsOpen) && (
        <div
          className="nav-overlay"
          onClick={() => {
            closeMenu();
            closeAllDropdowns();
          }}
        />
      )}
    </nav>
  );
};

export default Navigation;
